import React from 'react';
import { formatJEDescription } from '../../utils/formatJEDescription';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { JournalEntryDto } from '../../services/journalEntries.service';

interface JournalEntryDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  entry: JournalEntryDto;
}

const JournalEntryDetailsDialog: React.FC<JournalEntryDetailsDialogProps> = ({
  open,
  onClose,
  entry
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const t = {
    title: isRTL ? 'تفاصيل القيد' : 'Journal Entry Details',
    basicInfo: isRTL ? 'معلومات أساسية' : 'Basic Information',
    entryNumber: isRTL ? 'رقم القيد:' : 'Entry Number:',
    date: isRTL ? 'التاريخ:' : 'Date:',
    type: isRTL ? 'النوع:' : 'Type:',
    status: isRTL ? 'الحالة:' : 'Status:',
    fiscalYear: isRTL ? 'السنة المالية:' : 'Fiscal Year:',
    createdBy: isRTL ? 'أنشئ بواسطة:' : 'Created By:',
    createdDate: isRTL ? 'تاريخ الإنشاء:' : 'Created Date:',
    postedDate: isRTL ? 'تاريخ الترحيل:' : 'Posted Date:',
    description: isRTL ? 'الوصف:' : 'Description:',
    notes: isRTL ? 'ملاحظات:' : 'Notes:',
    details: isRTL ? 'التفاصيل' : 'Details',
    account: isRTL ? 'الحساب' : 'Account',
    debit: isRTL ? 'مدين' : 'Debit',
    credit: isRTL ? 'دائن' : 'Credit',
    costCenter: isRTL ? 'مركز التكلفة' : 'Cost Center',
    lineDescription: isRTL ? 'البيان' : 'Description',
    total: isRTL ? 'الإجمالي:' : 'Total:',
    close: isRTL ? 'إغلاق' : 'Close',
    manual: isRTL ? 'يدوي' : 'Manual',
    auto: isRTL ? 'تلقائي' : 'Auto',
    posted: isRTL ? 'مرحل' : 'Posted',
    draft: isRTL ? 'مسودة' : 'Draft',
    cancelled: isRTL ? 'ملغي' : 'Cancelled',
    entryBalanced: isRTL ? 'القيد متوازن' : 'Entry is balanced',
    entryUnbalanced: isRTL ? 'القيد غير متوازن' : 'Entry is unbalanced',
    difference: isRTL ? 'الفرق:' : 'Difference:'
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Posted':
        return 'success';
      case 'Draft':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Posted':
        return t.posted;
      case 'Draft':
        return t.draft;
      case 'Cancelled':
        return t.cancelled;
      default:
        return status;
    }
  };

  const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
      <Box sx={{ flex: '0 0 33%' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" component="div">
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t.title}
      </DialogTitle>
      <DialogContent>
        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Typography variant="h6" gutterBottom>
            {t.basicInfo}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <InfoRow
                label={t.entryNumber}
                value={entry.entryNumber}
              />
              <InfoRow
                label={t.date}
                value={new Date(entry.entryDate).toLocaleDateString('en-GB')}
              />
              <InfoRow
                label={t.type}
                value={entry.entryType === 'Manual' ? t.manual : t.auto}
              />
              <InfoRow
                label={t.status}
                value={<Chip label={getStatusLabel(entry.status)} color={getStatusColor(entry.status)} size="small" />}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <InfoRow
                label={t.fiscalYear}
                value={entry.fiscalPeriodName || '-'}
              />
              <InfoRow
                label={t.createdBy}
                value={entry.createdByName || entry.createdBy}
              />
              <InfoRow
                label={t.createdDate}
                value={new Date(entry.createdDate).toLocaleString()}
              />
              {entry.postedDate && (
                <InfoRow
                  label={t.postedDate}
                  value={new Date(entry.postedDate).toLocaleString()}
                />
              )}
            </Box>
          </Box>

          {entry.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t.description}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {isRTL ? entry.descriptionAr || entry.description : entry.description}
              </Typography>
            </Box>
          )}

          {entry.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t.notes}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {entry.notes}
              </Typography>
            </Box>
          )}
        </Paper>

        <Typography variant="h6" gutterBottom>
          {t.details}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="5%">#</TableCell>
                <TableCell width="30%">{t.account}</TableCell>
                <TableCell width="15%" align="right">{t.debit}</TableCell>
                <TableCell width="15%" align="right">{t.credit}</TableCell>
                <TableCell width="20%">{t.costCenter}</TableCell>
                <TableCell width="15%">{t.lineDescription}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entry.details.map((detail) => (
                <TableRow key={detail.entryDetailId}>
                  <TableCell>{detail.lineNumber}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {detail.accountCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isRTL ? detail.accountNameAr || detail.accountName : detail.accountName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {detail.debit > 0 ? detail.debit.toFixed(3) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {detail.credit > 0 ? detail.credit.toFixed(3) : '-'}
                  </TableCell>
                  <TableCell>
                    {detail.costCenterCode ? (
                      <>
                        <Typography variant="body2">
                          {detail.costCenterCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {detail.costCenterName}
                        </Typography>
                      </>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatJEDescription(detail.description, isRTL, detail.accountName, detail.accountNameAr)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={2} align="right">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t.total}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {entry.totalDebit.toFixed(3)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {entry.totalCredit.toFixed(3)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {Math.abs(entry.totalDebit - entry.totalCredit) < 0.001 ? (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark" align="center">
              ✓ {t.entryBalanced}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.dark" align="center">
              ✗ {t.entryUnbalanced} - {t.difference} {Math.abs(entry.totalDebit - entry.totalCredit).toFixed(3)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalEntryDetailsDialog;